import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'

/**
 * usePrep — fetch the typed prep tree for a session.
 *
 * Mirrors the shape of `usePairs.js`. Returns cards + bullets and mutation
 * helpers: updatePrepCard, addBullet, updateBullet, removeBullet,
 * reorderBullets, submitPrep.
 *
 * The `ownerUserId` param is required — the backend enforces that the caller
 * is the prep owner (report) or their manager.
 *
 * Phase E: frontend hook. Consumes Phase D backend handlers/prep.py.
 *
 * onPrepChanged subscription: Phase E follow-up — backend schema work needed
 * to add the subscription type. Left as a stub comment here; the hook polls
 * manually via `reload()` instead.
 */

const GET_PREP = `
  query GetPrep($prepId: ID!, $ownerUserId: ID!) {
    getPrep(prepId: $prepId, ownerUserId: $ownerUserId) {
      prepId sessionId submittedAt isVisibleToManager
      cards {
        prepCardId kind order isCore isVisible state
        bullets {
          bulletId text suffix suffixKind goalId order
        }
      }
    }
  }
`

const UPDATE_PREP_CARD = `
  mutation UpdatePrepCard($prepCardId: ID!, $input: UpdatePrepCardInput!) {
    updatePrepCard(prepCardId: $prepCardId, input: $input) {
      prepCardId kind order isCore isVisible state
    }
  }
`

const ADD_BULLET = `
  mutation AddBullet($prepCardId: ID!, $input: AddBulletInput!) {
    addBullet(prepCardId: $prepCardId, input: $input) {
      bulletId text suffix suffixKind goalId order
    }
  }
`

const UPDATE_BULLET = `
  mutation UpdateBullet($bulletId: ID!, $input: UpdateBulletInput!) {
    updateBullet(bulletId: $bulletId, input: $input) {
      bulletId text suffix suffixKind goalId order
    }
  }
`

const REMOVE_BULLET = `
  mutation RemoveBullet($bulletId: ID!) {
    removeBullet(bulletId: $bulletId)
  }
`

const REORDER_BULLETS = `
  mutation ReorderBullets($prepCardId: ID!, $bulletIds: [ID!]!) {
    reorderBullets(prepCardId: $prepCardId, bulletIds: $bulletIds) {
      bulletId order
    }
  }
`

const SUBMIT_PREP = `
  mutation SubmitPrep($prepId: ID!) {
    submitPrep(prepId: $prepId) {
      prepId submittedAt isVisibleToManager
    }
  }
`

export function usePrep(prepId, ownerUserId, { enabled = true } = {}) {
  const [prep, setPrep] = useState(null)
  const [loading, setLoading] = useState(!!(prepId && ownerUserId && enabled))
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!prepId || !ownerUserId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_PREP, { prepId, ownerUserId })
      setPrep(data.getPrep)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [prepId, ownerUserId])

  useEffect(() => {
    if (enabled && prepId && ownerUserId) load()
  }, [load, enabled, prepId, ownerUserId])

  const updatePrepCard = useCallback(async (prepCardId, input) => {
    const data = await appsyncQuery(UPDATE_PREP_CARD, { prepCardId, input })
    const updated = data.updatePrepCard
    setPrep((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        cards: prev.cards.map((c) => c.prepCardId === prepCardId ? { ...c, ...updated } : c),
      }
    })
    return updated
  }, [])

  const addBullet = useCallback(async (prepCardId, input) => {
    const data = await appsyncQuery(ADD_BULLET, { prepCardId, input })
    const bullet = data.addBullet
    setPrep((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        cards: prev.cards.map((c) =>
          c.prepCardId === prepCardId
            ? { ...c, bullets: [...(c.bullets ?? []), bullet] }
            : c,
        ),
      }
    })
    return bullet
  }, [])

  const updateBullet = useCallback(async (bulletId, prepCardId, input) => {
    const data = await appsyncQuery(UPDATE_BULLET, { bulletId, input })
    const updated = data.updateBullet
    setPrep((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        cards: prev.cards.map((c) =>
          c.prepCardId === prepCardId
            ? { ...c, bullets: (c.bullets ?? []).map((b) => b.bulletId === bulletId ? { ...b, ...updated } : b) }
            : c,
        ),
      }
    })
    return updated
  }, [])

  const removeBullet = useCallback(async (bulletId, prepCardId) => {
    await appsyncQuery(REMOVE_BULLET, { bulletId })
    setPrep((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        cards: prev.cards.map((c) =>
          c.prepCardId === prepCardId
            ? { ...c, bullets: (c.bullets ?? []).filter((b) => b.bulletId !== bulletId) }
            : c,
        ),
      }
    })
  }, [])

  const reorderBullets = useCallback(async (prepCardId, bulletIds) => {
    const data = await appsyncQuery(REORDER_BULLETS, { prepCardId, bulletIds })
    const reordered = data.reorderBullets
    setPrep((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        cards: prev.cards.map((c) => {
          if (c.prepCardId !== prepCardId) return c
          const orderMap = Object.fromEntries(reordered.map((r) => [r.bulletId, r.order]))
          const sorted = [...(c.bullets ?? [])].sort((a, b) => (orderMap[a.bulletId] ?? 0) - (orderMap[b.bulletId] ?? 0))
          return { ...c, bullets: sorted }
        }),
      }
    })
  }, [])

  const submitPrep = useCallback(async () => {
    if (!prepId) return
    const data = await appsyncQuery(SUBMIT_PREP, { prepId })
    const updated = data.submitPrep
    setPrep((prev) => prev ? { ...prev, ...updated } : prev)
    return updated
  }, [prepId])

  return {
    prep,
    loading,
    error,
    reload: load,
    updatePrepCard,
    addBullet,
    updateBullet,
    removeBullet,
    reorderBullets,
    submitPrep,
  }
}
